import { useQuery } from '@tanstack/react-query';
import { useZoneContext } from '@/auth/zoneProvider';
import { useSitesWithLatestData } from '@/api/hooks/useSites';
import { getHistoricalData, getStatisticalData } from '@/api/historical-data';
import { AnalyticsDataPoint, AnalyticsFilters } from '../types/analytics';
import { POWER_METRICS, TEMPERATURE_METRICS, formatDateForAPI, aggregateToDaily, getAllCalculatedData } from '../utils/data-processing';
import { useMemo } from 'react';

interface UseAnalyticsDataParams {
  filters: AnalyticsFilters;
  enabled?: boolean;
}

interface UseAnalyticsDataResult {
  data: AnalyticsDataPoint[];
  isLoading: boolean;
  error: any;
  refetch: () => void;
}

export function useAnalyticsData({ filters, enabled = true }: UseAnalyticsDataParams): UseAnalyticsDataResult {
  const { zone } = useZoneContext();
  const { data: sitesData } = useSitesWithLatestData();
  
  const siteId = useMemo(() => {
    return sitesData?.data?.[0]?.id;
  }, [sitesData]);

  // Use zone ID with fallback - check both id and device_id
  const zoneId = zone?.id || zone?.device_id || 'zone_main';

  const isEnabled = enabled && !!zoneId; // Allow fetching even without siteId (will skip outdoor temp)
  
  const query = useQuery({
    queryKey: [
      'analytics-data',
      zoneId,
      siteId,
      filters.mode, // Include mode to separate cached data
      'all-base-metrics', // Query all base metrics regardless of selection
      filters.dateRange.start,
      filters.dateRange.end,
      filters.timeAggregation,
      filters.__forceRefreshRange1 // Include force refresh to trigger new API calls
    ],
    queryFn: async () => {
      if (!zoneId) {
        return [];
      }

      try {
        // Use different data fetching strategy based on mode
        if (filters.mode === 'energy') {
          return await fetchStatisticalAnalyticsData(
            zoneId,
            filters.dateRange.start,
            filters.dateRange.end,
            filters.timeAggregation,
            siteId
          );
        } else {
          return await fetchAggregatedAnalyticsData(
            zoneId,
            filters.dateRange.start,
            filters.dateRange.end,
            filters.timeAggregation,
            siteId
          );
        }
      } catch (error) {
        throw error;
      }
    },
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      if (error?.status && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
  });

  return {
    data: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch
  };
}

export function useComparisonAnalyticsData({ filters, enabled = true }: UseAnalyticsDataParams): {
  range1Data: AnalyticsDataPoint[];
  range2Data: AnalyticsDataPoint[];
  isLoading: boolean;
  error: any;
  range1Loading: boolean;
  range2Loading: boolean;
  range1Error: any;
  range2Error: any;
} {
  const { zone } = useZoneContext();
  const { data: sitesData } = useSitesWithLatestData();
  
  const siteId = useMemo(() => {
    return sitesData?.data?.[0]?.id;
  }, [sitesData]);

  // Use zone ID with fallback - check both id and device_id
  const zoneId = zone?.id || zone?.device_id || 'zone_main';
  
  // Query for range 1 (primary date range)
  const range1Query = useQuery({
    queryKey: [
      'analytics-comparison-range1',
      zoneId,
      siteId,
      filters.mode, // Include mode to separate cached data
      'all-base-metrics',
      filters.dateRange.start,
      filters.dateRange.end,
      filters.timeAggregation,
      filters.__forceRefreshRange1 // Include force refresh for Range 1
    ],
    queryFn: async () => {
      if (!zoneId) {
        return [];
      }

      if (filters.mode === 'energy') {
        return await fetchStatisticalAnalyticsData(
          zoneId,
          filters.dateRange.start,
          filters.dateRange.end,
          filters.timeAggregation,
          siteId
        );
      } else {
        return await fetchAggregatedAnalyticsData(
          zoneId,
          filters.dateRange.start,
          filters.dateRange.end,
          filters.timeAggregation,
          siteId
        );
      }
    },
    enabled: enabled && !!zoneId, // Allow fetching even without siteId
    staleTime: 5 * 60 * 1000,
  });

  // Query for range 2 (comparison date range)
  const range2Query = useQuery({
    queryKey: [
      'analytics-comparison-range2',
      zoneId,
      siteId,
      filters.mode, // Include mode to separate cached data
      'all-base-metrics',
      filters.comparison.range2?.start,
      filters.comparison.range2?.end,
      filters.timeAggregation,
      filters.__forceRefreshRange2 // Include force refresh for Range 2
    ],
    queryFn: async () => {
      console.log('Range 2 Query Triggered:', {
        zoneId,
        metrics: filters.selectedMetrics,
        range2: filters.comparison.range2,
        timeAggregation: filters.timeAggregation,
        forceRefresh: filters.__forceRefreshRange2
      });
      
      if (!zoneId || !filters.comparison.range2) {
        console.log('Range 2 Query Skipped - Missing requirements');
        return [];
      }

      let result;
      if (filters.mode === 'energy') {
        result = await fetchStatisticalAnalyticsData(
          zoneId,
          filters.comparison.range2.start,
          filters.comparison.range2.end,
          filters.timeAggregation,
          siteId
        );
      } else {
        result = await fetchAggregatedAnalyticsData(
          zoneId,
          filters.comparison.range2.start,
          filters.comparison.range2.end,
          filters.timeAggregation,
          siteId
        );
      }
      
      console.log('Range 2 Query Result:', result?.length || 0, 'data points');
      return result;
    },
    enabled: enabled && 
      !!zoneId && 
      filters.comparison.enabled && 
      !!filters.comparison.range2,
    staleTime: 5 * 60 * 1000,
  });

  return {
    range1Data: range1Query.data || [],
    range2Data: range2Query.data || [],
    isLoading: range1Query.isLoading || range2Query.isLoading,
    error: range1Query.error || range2Query.error,
    range1Loading: range1Query.isLoading,
    range2Loading: range2Query.isLoading,
    range1Error: range1Query.error,
    range2Error: range2Query.error
  };
}

async function fetchAggregatedAnalyticsData(
  zoneId: string,
  startDate: string,
  endDate: string,
  timeAggregation: string,
  siteId?: string
): Promise<AnalyticsDataPoint[]> {
  try {
    const allMetrics = [...POWER_METRICS, ...TEMPERATURE_METRICS];
    const requests = [];

    // Query ALL base metrics needed for calculations
    const baseMetricsToQuery = allMetrics.filter(m => m.deviceId !== 'calculated');
    
    for (const metricConfig of baseMetricsToQuery) {

      // Skip outdoor temperature if no siteId available
      if (metricConfig.key === 'outdoor_temp' && !siteId) {
        continue;
      }
      
      // Use dynamic siteId for outdoor temperature
      const deviceId = metricConfig.key === 'outdoor_temp' ? siteId : metricConfig.deviceId;

      const request = getHistoricalData({
        device_id: deviceId,
        datapoints: [metricConfig.datapoint],
        start_timestamp: formatDateForAPI(startDate, false),
        end_timestamp: formatDateForAPI(endDate, true),
        resampling: timeAggregation,
        tz: 'Asia/Bangkok'
      }).catch(() => null);

      requests.push(request.then(data => ({ metricKey: metricConfig.key, data })));
    }

    // Execute all requests in parallel
    const results = await Promise.all(requests);
    
    // Transform results into AnalyticsDataPoint format
    const analyticsData: AnalyticsDataPoint[] = [];
    
    results.forEach(({ metricKey, data }) => {
      if (!data) return; // Skip failed requests
      
      // Handle different API response formats
      let dataArray = [];
      
      if (Array.isArray(data)) {
        dataArray = data;
      } else if (data?.data && Array.isArray(data.data)) {
        dataArray = data.data;
      } else if (data?.data?.data && Array.isArray(data.data.data)) {
        dataArray = data.data.data;
      }
      
      if (dataArray.length > 0) {
        dataArray.forEach(deviceData => {
          if (deviceData?.values && Array.isArray(deviceData.values)) {
            deviceData.values.forEach(point => {
              if (point && 
                  typeof point.value === 'number' && 
                  !isNaN(point.value) &&
                  point.timestamp) {
                analyticsData.push({
                  timestamp: point.timestamp,
                  value: point.value,
                  metric: metricKey
                });
              }
            });
          }
        });
      }
    });

    return analyticsData;
  } catch (error) {
    throw error;
  }
}

async function fetchStatisticalAnalyticsData(
  zoneId: string,
  startDate: string,
  endDate: string,
  timeAggregation: string,
  siteId?: string
): Promise<AnalyticsDataPoint[]> {
  try {
    const allMetrics = [...POWER_METRICS, ...TEMPERATURE_METRICS];
    const requests = [];

    // Query ALL base metrics needed for calculations
    const baseMetricsToQuery = allMetrics.filter(m => m.deviceId !== 'calculated');
    
    for (const metricConfig of baseMetricsToQuery) {
      // Skip outdoor temperature if no siteId available
      if (metricConfig.key === 'outdoor_temp' && !siteId) {
        continue;
      }
      
      // Use dynamic siteId for outdoor temperature
      const deviceId = metricConfig.key === 'outdoor_temp' ? siteId : metricConfig.deviceId;

      const request = getStatisticalData({
        device_id: deviceId,
        datapoints: [metricConfig.datapoint],
        start_timestamp: formatDateForAPI(startDate, false),
        end_timestamp: formatDateForAPI(endDate, true),
        table_name: 'statistic_data_1hour', // Always fetch hourly data
        tz: 'Asia/Bangkok'
      }).catch(() => null);

      requests.push(request.then(data => ({ metricKey: metricConfig.key, data })));
    }

    // Execute all requests in parallel
    const results = await Promise.all(requests);
    
    // Transform results into AnalyticsDataPoint format
    const analyticsData: AnalyticsDataPoint[] = [];
    
    // Log all results first to see what we're getting
    console.log('Statistical API results:', results.map(r => ({ 
      metric: r.metricKey, 
      hasData: !!r.data?.data,
      dataLength: r.data?.data?.length || 0 
    })));
    
    results.forEach(({ metricKey, data }) => {
      if (!data?.data) {
        console.log(`No data for metric ${metricKey}`);
        return; // Skip failed requests
      }
      
      const dataArray = data.data;
      console.log(`Processing statistical data for ${metricKey}:`, dataArray.length, 'devices');
      
      dataArray.forEach(deviceData => {
        if (deviceData?.records && Array.isArray(deviceData.records)) {
          console.log(`Device ${deviceData.device_id} has ${deviceData.records.length} records for ${metricKey}`);
          
          deviceData.records.forEach(record => {
            if (record && 
                typeof record.mean_value === 'number' && 
                !isNaN(record.mean_value) &&
                record.timestamp) {
              analyticsData.push({
                timestamp: record.timestamp,
                value: record.mean_value, // Use mean_value for statistical data
                metric: metricKey
              });
              
              // Log first few records for debugging
              if (analyticsData.length <= 5) {
                console.log(`Added data point:`, {
                  timestamp: record.timestamp,
                  value: record.mean_value,
                  metric: metricKey
                });
              }
            }
          });
        }
      });
    });

    console.log(`Statistical data processing complete. Total data points: ${analyticsData.length}`);
    
    // Calculate derived metrics for hourly data first
    const analyticsWithCalculated = getAllCalculatedData(analyticsData);
    console.log(`After calculated metrics: ${analyticsWithCalculated.length} data points`);
    
    // Apply daily aggregation if timeAggregation is 'day'
    if (timeAggregation === 'day') {
      const dailyData = aggregateToDaily(analyticsWithCalculated);
      console.log(`After daily aggregation: ${dailyData.length} data points`);
      
      return dailyData;
    }
    
    return analyticsWithCalculated;
  } catch (error) {
    console.error('Error in fetchStatisticalAnalyticsData:', error);
    throw error;
  }
}